import { createSlice } from '@reduxjs/toolkit';

import type { InternoAndamento, InternoPendencia, InternoPendenciaFiltro } from '../../types';
import {
  excluirInternoAndamento,
  fetchInternoAndamentos,
  fetchInternoPendencias,
  removerSeloAndamento,
  responderInternoPendencias,
  tornarMensagemAndamento,
} from './actions';

const URGENCIAS_URGENTES = new Set(['VENCIDA', 'VENCE_HOJE', 'VENCE_EM_BREVE']);

const ehPendenciaUrgente = (item: InternoPendencia): boolean =>
  item.status !== 'RESOLVIDA' &&
  item.status !== 'CANCELADA' &&
  item.status !== 'ENCERRADA_PELA_SENTENCA' &&
  !!item.urgencia &&
  URGENCIAS_URGENTES.has(item.urgencia);

interface ProcessoInternoState {
  processoId: string | null;
  andamentos: InternoAndamento[];
  pendencias: InternoPendencia[];
  pendenciasFiltro: InternoPendenciaFiltro;
  pendenciasContagem: { todas: number; urgentes: number; concluidas: number } | null;
  // `processoId` sozinho não separa duas buscas do MESMO processo: trocar de sub-aba dispara
  // outra busca, e a resposta antiga chegando depois repunha a lista do filtro abandonado.
  pendenciasRequestId: string | null;
  responderRequestId: string | null;
  isLoadingAndamentos: boolean;
  isLoadingPendencias: boolean;
  errorAndamentos: string | null;
  errorPendencias: string | null;
  isMutating: boolean;
}

const initialState: ProcessoInternoState = {
  processoId: null,
  andamentos: [],
  pendencias: [],
  pendenciasFiltro: 'todas',
  pendenciasContagem: null,
  pendenciasRequestId: null,
  responderRequestId: null,
  isLoadingAndamentos: false,
  isLoadingPendencias: false,
  errorAndamentos: null,
  errorPendencias: null,
  isMutating: false,
};

// A resposta em voo pertence ao processo que saiu de cena, e o guard de `requestId` vai
// descartar a conclusão dela: sem zerar aqui, `isMutating` ficaria preso em `true` e o botão
// Responder nasceria desabilitado no processo novo.
const descartarMutacaoEmVoo = (state: ProcessoInternoState): void => {
  state.isMutating = false;
  state.responderRequestId = null;
};

// Duas perguntas diferentes: o `requestId` separa a resposta que ainda está em voo da que já
// foi substituída por outra, e o processo separa a aba que pediu da que está na tela — sem ele,
// uma conclusão do processo anterior mexeria em `isMutating` e `pendencias` do
// processo novo, porque `processoArbitralId` é o mesmo campo que a aba manda no argumento.
const ehRespostaAtiva = (
  state: ProcessoInternoState,
  meta: { requestId: string; arg: { processoArbitralId: string } },
): boolean =>
  state.responderRequestId === meta.requestId && state.processoId === meta.arg.processoArbitralId;

const processoInternoSlice = createSlice({
  name: 'chatAtendimentoProcessoInterno',
  initialState,
  reducers: {
    resetProcessoInterno: () => initialState,
    setPendenciasFiltro: (state, action: { payload: InternoPendenciaFiltro }) => {
      state.pendenciasFiltro = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchInternoAndamentos.pending, (state, action) => {
        if (state.processoId !== action.meta.arg) {
          state.andamentos = [];
          state.pendencias = [];
          state.errorPendencias = null;
          state.pendenciasFiltro = 'todas';
          state.pendenciasContagem = null;
          descartarMutacaoEmVoo(state);
        }
        state.processoId = action.meta.arg;
        state.isLoadingAndamentos = true;
        state.errorAndamentos = null;
      })
      .addCase(fetchInternoAndamentos.fulfilled, (state, action) => {
        if (state.processoId !== action.payload.processoId) return;
        state.isLoadingAndamentos = false;
        state.andamentos = action.payload.items;
      })
      .addCase(fetchInternoAndamentos.rejected, (state, action) => {
        if (state.processoId !== action.meta.arg) return;
        state.isLoadingAndamentos = false;
        state.errorAndamentos = action.payload ?? 'Erro ao carregar andamentos';
      })
      .addCase(fetchInternoPendencias.pending, (state, action) => {
        if (state.processoId !== action.meta.arg.processoId) {
          state.andamentos = [];
          state.pendencias = [];
          state.errorAndamentos = null;
          state.pendenciasContagem = null;
          descartarMutacaoEmVoo(state);
        }
        state.processoId = action.meta.arg.processoId;
        state.pendenciasFiltro = action.meta.arg.filtro;
        state.pendenciasRequestId = action.meta.requestId;
        state.isLoadingPendencias = true;
        state.errorPendencias = null;
      })
      .addCase(fetchInternoPendencias.fulfilled, (state, action) => {
        if (state.pendenciasRequestId !== action.meta.requestId) return;
        state.isLoadingPendencias = false;
        const { filtro, data } = action.payload;
        state.pendencias =
          filtro === 'urgentes' ? data.items.filter(ehPendenciaUrgente) : data.items;

        // O servidor conta `ativas`/`finalizadas`/`todas` do processo inteiro, mas não conhece
        // "urgente" — urgência é derivada da `urgencia` de cada item, aqui no cliente. Só dá
        // para recontá-la quando o recorte carregado contém as ativas (`todas`/`urgentes`);
        // vindo de `concluidas`, preserva o último valor conhecido em vez de zerar a sub-aba.
        state.pendenciasContagem = data.contagem
          ? {
              todas: data.contagem.todas,
              urgentes:
                filtro === 'todas'
                  ? data.items.filter(ehPendenciaUrgente).length
                  : filtro === 'urgentes'
                    ? state.pendencias.length
                    : (state.pendenciasContagem?.urgentes ?? 0),
              concluidas: data.contagem.finalizadas,
            }
          : null;
      })
      .addCase(fetchInternoPendencias.rejected, (state, action) => {
        if (state.pendenciasRequestId !== action.meta.requestId) return;
        state.isLoadingPendencias = false;
        state.errorPendencias = action.payload ?? 'Erro ao carregar pendências';
      })
      .addCase(tornarMensagemAndamento.pending, (state) => {
        state.isMutating = true;
      })
      .addCase(tornarMensagemAndamento.fulfilled, (state, action) => {
        state.isMutating = false;
        if (!state.andamentos.some((item) => item.id === action.payload.id)) {
          state.andamentos = [action.payload, ...state.andamentos];
        }
      })
      .addCase(tornarMensagemAndamento.rejected, (state) => {
        state.isMutating = false;
      })
      .addCase(removerSeloAndamento.pending, (state) => {
        state.isMutating = true;
      })
      .addCase(removerSeloAndamento.fulfilled, (state, action) => {
        state.isMutating = false;
        state.andamentos = state.andamentos.filter(
          (item) => item.chatMensagemId !== action.payload.mensagemId,
        );
      })
      .addCase(removerSeloAndamento.rejected, (state) => {
        state.isMutating = false;
      })
      .addCase(excluirInternoAndamento.pending, (state) => {
        state.isMutating = true;
      })
      .addCase(excluirInternoAndamento.fulfilled, (state, action) => {
        state.isMutating = false;
        state.andamentos = state.andamentos.filter(
          (item) => item.id !== action.payload.andamentoId,
        );
      })
      .addCase(excluirInternoAndamento.rejected, (state) => {
        state.isMutating = false;
      })
      .addCase(responderInternoPendencias.pending, (state, action) => {
        state.responderRequestId = action.meta.requestId;
        state.isMutating = true;
      })
      .addCase(responderInternoPendencias.fulfilled, (state, action) => {
        if (!ehRespostaAtiva(state, action.meta)) return;
        state.isMutating = false;
        state.responderRequestId = null;
        // 200 pode ser parcial: só sai da lista o que o core confirmou em `movimentadas`. O que
        // voltou em `naoMovimentadas` continua aberto — a aba lê o resultado do `unwrap` para
        // avisar, senão ela dava por respondida uma pendência que o core recusou.
        const movimentadas = new Set(action.payload.movimentadas);
        state.pendencias = state.pendencias.filter((item) => !movimentadas.has(item.id));

        // A contagem das sub-abas é do processo inteiro, então a remoção acima a deixava
        // divergente da lista até a próxima busca. `RESOLVER` leva a pendência a `RESOLVIDA`, que
        // o core soma em `finalizadas` sem tirá-la de `todas` (o balde `todas` conta todo status,
        // ver `somarContagem` no core) — por isso só `concluidas` sobe e `todas` fica como veio do
        // servidor. `urgentes` continua derivada aqui, pela mesma regra do fetch.
        if (state.pendenciasContagem && movimentadas.size > 0) {
          state.pendenciasContagem = {
            todas: state.pendenciasContagem.todas,
            urgentes:
              state.pendenciasFiltro === 'concluidas'
                ? state.pendenciasContagem.urgentes
                : state.pendencias.filter(ehPendenciaUrgente).length,
            concluidas: state.pendenciasContagem.concluidas + movimentadas.size,
          };
        }
      })
      .addCase(responderInternoPendencias.rejected, (state, action) => {
        if (!ehRespostaAtiva(state, action.meta)) return;
        state.isMutating = false;
        state.responderRequestId = null;
      });
  },
});

export const { resetProcessoInterno, setPendenciasFiltro } = processoInternoSlice.actions;
export const processoInternoReducer = processoInternoSlice.reducer;
export type { ProcessoInternoState };
